import React, { useEffect, useId, useRef } from 'react';
import { useEscToClose } from '../../hooks/useEscToClose';
import './FormModal.css';

/**
 * FormModal — shared modal-chrome primitive for the crypto-trading UI.
 *
 * Owns the canonical modal chrome (overlay, header with title + `×` close
 * button, body, and a footer of `modal-pill-button` actions) so every modal
 * renders identical chrome instead of re-implementing it by hand.
 *
 * The pill-class convention is enforced by the type system: each footer action
 * declares a `variant` (`primary` | `destructive` | `secondary`) and the
 * primitive — not the caller — maps it to the canonical pill class. Callers
 * cannot pass a raw `className` for a footer action, so the next modal author
 * cannot reach for the wrong pill class (the drift the CTR-016 review found).
 *
 * Variant → pill class (matches the convention documented at
 * `CancelTradeGroupModal.css:342-367`):
 * - primary     → modal-pill-button modal-pill-primary   (constructive: Create/Resolve/Save)
 * - destructive → modal-pill-button modal-pill-apply     (irreversible: Apply/Delete)
 * - secondary   → modal-pill-button modal-pill-cancel    (dismiss / navigation)
 *
 * Submit-driven footers: pass `onSubmit` and the body+footer render inside a
 * `<form>`; `primary` actions become `type="submit"` so modals whose primary
 * action is a form submit (e.g. ManualTransactionModal) migrate without
 * inverting their submit semantics.
 *
 * Follows the shared-primitive pattern of `DataTable.tsx` (named export,
 * co-located CSS).
 */

export type FormModalAction = {
  /** visible button text */
  label: string;
  /** discriminator that fully determines the pill class (no className escape hatch) */
  variant: 'primary' | 'destructive' | 'secondary';
  /** click handler; omit for submit-driven primary actions inside a `<form>` */
  onClick?: () => void;
  /** caller-supplied disabled state; composed (OR) with the `pending` prop */
  disabled?: boolean;
  /** button type; defaults to 'submit' for primary actions inside a form, else 'button' */
  type?: 'button' | 'submit';
};

const VARIANT_CLASS: Record<FormModalAction['variant'], string> = {
  primary: 'modal-pill-button modal-pill-primary',
  destructive: 'modal-pill-button modal-pill-apply',
  secondary: 'modal-pill-button modal-pill-cancel',
};

export interface FormModalProps {
  /** header title */
  title: string;
  /** dismissal callback — wired to the `×` close button and Esc (gated by `pending`) */
  onClose: () => void;
  /** when true, disables all actions + the close button and gates Esc */
  pending?: boolean;
  /** modal body content (form fields) */
  children: React.ReactNode;
  /** footer buttons, each variant-typed */
  actions: FormModalAction[];
  /** width modifier; 'wide' applies max-width: 800px */
  size?: 'default' | 'wide';
  /**
   * Extra class(es) appended to the `modal-content` container for modals with a
   * bespoke width/appearance (e.g. pass a modal's existing content class to
   * preserve its exact size/background). Governs only the content container —
   * never action/button styling — so it introduces no pill-class escape hatch.
   */
  contentClassName?: string;
  /** when provided, body+footer render inside a `<form>` and primary actions submit it */
  onSubmit?: (e: React.FormEvent) => void;
  /**
   * Optional content rendered AFTER the footer (inside the `<form>` when
   * `onSubmit` is set) — e.g. a server-error banner that must sit below the
   * action buttons. Kept out of `children` so the error never lands above the
   * footer in the body.
   */
  errorSlot?: React.ReactNode;
}

export function FormModal({
  title,
  onClose,
  pending = false,
  children,
  actions,
  size = 'default',
  contentClassName,
  onSubmit,
  errorSlot,
}: FormModalProps): React.ReactElement {
  useEscToClose(onClose, { disabled: pending });

  const titleId = useId();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Move focus into the dialog on mount so keyboard and screen-reader users
    // start inside the modal rather than on the underlying page.
    contentRef.current?.focus();
  }, []);

  const contentClass = [
    'modal-content',
    size === 'wide' ? 'modal-content--wide' : null,
    contentClassName ?? null,
  ]
    .filter(Boolean)
    .join(' ');

  const renderAction = (action: FormModalAction) => {
    // Primary actions inside a form default to type="submit" so the form's
    // onSubmit fires; everywhere else default to "button". The action's own
    // `type` always wins when supplied.
    const defaultType: 'button' | 'submit' =
      onSubmit && action.variant === 'primary' ? 'submit' : 'button';
    return (
      <button
        key={action.label}
        type={action.type ?? defaultType}
        className={VARIANT_CLASS[action.variant]}
        onClick={action.onClick}
        disabled={pending || action.disabled}
      >
        {action.label}
      </button>
    );
  };

  const body = <div className="modal-body">{children}</div>;
  const footer = (
    <div className="modal-footer">{actions.map(renderAction)}</div>
  );

  return (
    <div
      className="modal-overlay"
      onClick={pending ? undefined : onClose}
    >
      <div
        ref={contentRef}
        className={contentClass}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="close-button"
            onClick={onClose}
            disabled={pending}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {onSubmit ? (
          <form onSubmit={onSubmit}>
            {body}
            {footer}
            {errorSlot}
          </form>
        ) : (
          <>
            {body}
            {footer}
            {errorSlot}
          </>
        )}
      </div>
    </div>
  );
}
